import React from 'react';

/**
 * MōMA Pokè logo — replica fedele del brand.
 * Cerchio blu royal con "MōMA" (serif) sopra e "Pokè" (serif) sotto.
 * Bordo bianco per staccarsi dal pattern a strisce.
 */
export const MomaMark = ({ size = 64, onStripes = false, className = '' }) => {
  const s = size;
  return (
    <svg
      width={s} height={s} viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ display: 'inline-block', flexShrink: 0 }}
      aria-label="MōMA Pokè"
    >
      <defs>
        <clipPath id="momaCircleClip">
          <circle cx="100" cy="100" r="96" />
        </clipPath>
      </defs>
      {/* Outer white ring (visible on stripes) */}
      {onStripes && <circle cx="100" cy="100" r="99" fill="#FFFFFF" />}
      {/* Main blue disc */}
      <circle cx="100" cy="100" r="96" fill="#1E4D9B" />
      {/* Inner subtle border for definition */}
      <circle cx="100" cy="100" r="93" fill="none" stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="1" />

      <g fill="#FFFFFF" fontFamily="'Playfair Display', Georgia, 'Times New Roman', serif" textAnchor="middle">
        {/* MōMA - wordmark */}
        <text
          x="100" y="102"
          dominantBaseline="alphabetic"
          fontWeight="500"
          fontSize="46"
          letterSpacing="1"
        >MōMA</text>
        {/* Divider stroke — small horizontal line between the two words for uniformity */}
        <line x1="80" y1="118" x2="120" y2="118" stroke="#FFFFFF" strokeWidth="1" opacity="0.6" />
        {/* Pokè */}
        <text
          x="100" y="150"
          dominantBaseline="alphabetic"
          fontWeight="400"
          fontSize="24"
          letterSpacing="0.5"
        >Pokè</text>
      </g>
    </svg>
  );
};

/**
 * Logo con marchio + eventuale testo laterale.
 * variant: 'mark' (solo cerchio), 'compact' (cerchio + testo piccolo), 'full' (cerchio grande + testo grande)
 */
export const Logo = ({ variant = 'compact', className = '', tone = 'auto' }) => {
  const isDark = tone === 'dark';
  const textColor = isDark ? 'text-white' : 'text-[--brand-primary]';

  if (variant === 'mark') {
    return <MomaMark size={44} className={className} />;
  }

  if (variant === 'full') {
    return (
      <div className={`flex items-center gap-4 ${className}`}>
        <MomaMark size={88} />
        <div className="leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          <div className={`font-bold text-4xl ${textColor}`} style={{ letterSpacing: '0.02em' }}>MōMA</div>
          <div className={`text-xl ${textColor} mt-1`}>Pokè</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <MomaMark size={42} />
      <div className="leading-[1.05]">
        <div className={`text-[17px] font-semibold ${textColor} flex items-baseline gap-1`} style={{ letterSpacing: '0.03em', fontFamily: "'Playfair Display', Georgia, serif" }}>
          <span>MōMA</span>
          <span className="font-normal opacity-90">Pokè</span>
        </div>
        <div className="text-[9px] uppercase tracking-[0.22em] text-[--brand-muted] mt-0.5">Fresh delivery</div>
      </div>
    </div>
  );
};

/**
 * Pattern a strisce verticali blu-bianco (signature brand background).
 * Da usare come sfondo di sezioni hero, hero-cards, ecc.
 */
export const StripesBackground = ({ className = '', density = 24, opacity = 1, dark = true }) => {
  const primary = dark ? '#1E4D9B' : '#FFFFFF';
  const secondary = dark ? '#FFFFFF' : '#1E4D9B';
  return (
    <div
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{
        backgroundImage: `repeating-linear-gradient(90deg, ${primary} 0, ${primary} ${density}px, ${secondary} ${density}px, ${secondary} ${density * 2}px)`,
        opacity,
      }}
    />
  );
};

/**
 * Big brand medallion: cerchio blu enorme centrato tra strisce bianco/blu.
 * Da usare nella hero della landing per rendere il brand ancora più forte.
 */
export const BrandMedallion = ({ size = 300, className = '' }) => (
  <div className={`relative flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
    <MomaMark size={size} />
  </div>
);
